import React from 'react';
import MicroLabel from '../components/MicroLabel';
import { pct, barWidth, dateDots } from '../format';

/**
 * Chapter 04 — You, against the market.
 *
 * Three bars: the portfolio, the S&P 500 and Bitcoin over the same window.
 *
 * Two honesty rules the design's placeholder bars couldn't express:
 *
 *  1. Benchmark values only exist on snapshots taken after benchmark tracking
 *     was added, so the window can be shorter than the portfolio's own history.
 *     The sub-label states the real date range rather than claiming "same
 *     period" for a period that isn't.
 *  2. Bars are measured on absolute magnitude with a visible floor, so a −30%
 *     never renders shorter than it is and never disappears; the sign lives in
 *     the label.
 */
function Comparison({ story }) {
  const benchmarks = story?.benchmarks || {};
  const { startDate, endDate } = benchmarks;

  const windowLabel = startDate && endDate
    ? `${dateDots(startDate)} — ${dateDots(endDate)}`
    : 'Same period';

  const rows = [
    {
      name: 'Your portfolio',
      sub: 'All time',
      value: benchmarks.portfolio,
      fill: 'var(--color-accent)',
      valueColor: 'var(--color-accent-700)',
    },
    {
      name: 'S&P 500',
      sub: windowLabel,
      value: benchmarks.sp500,
      fill: 'var(--color-text)',
      valueColor: 'var(--color-text)',
    },
    {
      name: 'Bitcoin',
      sub: windowLabel,
      value: benchmarks.bitcoin,
      fill: 'var(--color-neutral-400)',
      valueColor: 'var(--color-text)',
    },
  ].filter((row) => Number.isFinite(row.value));

  const max = Math.max(...rows.map((row) => Math.abs(row.value)), 0);

  return (
    <>
      <h2 className="story-h2">You, against the market.</h2>

      {rows.length === 0 ? (
        <div className="story-empty">
          <p className="story-empty-text">
            No comparable history yet — the market race starts once you have two
            snapshots.
          </p>
        </div>
      ) : (
        <div className="story-compare">
          {rows.map((row) => (
            <div className="story-compare-row" key={row.name}>
              <div>
                <div className="story-compare-name">{row.name}</div>
                <MicroLabel style={{ marginTop: 4 }}>{row.sub}</MicroLabel>
              </div>

              <div className="story-compare-track">
                <div
                  className="story-compare-fill"
                  style={{ width: `${barWidth(row.value, max)}%`, background: row.fill }}
                  aria-hidden="true"
                />
              </div>

              <div className="story-compare-value" style={{ color: row.valueColor }}>
                {pct(row.value)}
              </div>
            </div>
          ))}

          {rows.length < 3 && (
            <MicroLabel style={{ marginTop: 24 }}>
              Benchmark history is still building — missing rows appear once snapshots
              carry market data.
            </MicroLabel>
          )}
        </div>
      )}
    </>
  );
}

export default Comparison;
